import { apiFetch } from './client';

/**
 * REPOSICIÓN — el inventario autogestionable (`/api/inventario/reposicion`).
 *
 * Nada de esto le escribe a un suplidor ni a un mensajero: el servidor prepara el pedido y sus
 * mensajes, el dueño dice que sí aquí, y mandarlos lo hace una persona.
 */

export type MotivoDeAviso = 'agotada' | 'bajo_minimo' | 'se_agota_pronto';
export type EstadoDePedido = 'PROPUESTO' | 'APROBADO' | 'ENCARGADO' | 'RECIBIDO' | 'DESCARTADO';

export interface SuplidorCorto {
  id: string;
  nombre: string;
  telefono?: string | null;
}

export interface PiezaPorAgotarse {
  varianteId: string;
  productoId: string;
  producto: string;
  codigo: string | null;
  talla: string;
  quedan: number;
  minimo: number;
  /** Vendidas en los últimos `diasDeVenta` días. */
  vendidas: number;
  /** Días que aguanta al ritmo de venta. `null` = no se ha vendido en ese tiempo. */
  dias: number | null;
  motivo: MotivoDeAviso;
  suplidores: SuplidorCorto[];
  enPedido: number;
  sugerido: number;
}

export interface LineaDePedido {
  id: string;
  varianteId: string;
  cantidad: number;
  recibida: number | null;
  productoId: string | null;
  producto: string;
  codigo: string | null;
  talla: string;
  quedan: number;
}

export interface Pedido {
  id: string;
  numero: string;
  estado: EstadoDePedido;
  fabricacion: boolean;
  suplidor: SuplidorCorto | null;
  quienBusca: string | null;
  nota: string | null;
  createdAt: string;
  aprobadoAt: string | null;
  encargadoAt: string | null;
  recibidoAt: string | null;
  lineas: LineaDePedido[];
  mensajes: { paraElSuplidor: string; paraQuienBusca: string | null };
}

export interface Lugares {
  lugares: Array<{ id: string; nombre: string; tipo: 'ALMACEN' | 'TIENDA' }>;
  porVariante: Record<string, { enAlmacen: number; porTienda: Record<string, number> }>;
}

export interface Tablero {
  seFabrica: boolean;
  diasDeVenta: number;
  porAgotarse: PiezaPorAgotarse[];
  pedidos: Pedido[];
  suplidores: SuplidorCorto[];
  suplidoresPorProducto: Record<string, string[]>;
  /** Solo en los negocios con almacén y tiendas. */
  lugares: Lugares | null;
}

const BASE = '/api/inventario/reposicion';

export const tablero = (): Promise<Tablero> => apiFetch<Tablero>(BASE);

export const configurarProducto = (
  productoId: string,
  body: { suplidorIds?: string[]; minimos?: Array<{ varianteId: string; minimo: number }> },
): Promise<{ ok: true }> => apiFetch(`${BASE}/productos/${productoId}`, { method: 'PUT', body });

export const prepararPedidos = (piezas?: Array<{ varianteId: string; cantidad: number }>): Promise<Pedido[]> =>
  apiFetch<Pedido[]>(`${BASE}/pedidos`, { method: 'POST', body: piezas ? { piezas } : {} });

export const editarPedido = (
  id: string,
  body: {
    suplidorId?: string | null;
    quienBusca?: string | null;
    nota?: string | null;
    lineas?: Array<{ id: string; cantidad: number }>;
  },
): Promise<Pedido> => apiFetch<Pedido>(`${BASE}/pedidos/${id}`, { method: 'PATCH', body });

export const responderPedido = (id: string, si: boolean, quienBusca?: string | null): Promise<Pedido> =>
  apiFetch<Pedido>(`${BASE}/pedidos/${id}/responder`, { method: 'POST', body: { si, quienBusca: quienBusca || null } });

export const marcarEncargado = (id: string): Promise<Pedido> =>
  apiFetch<Pedido>(`${BASE}/pedidos/${id}/encargado`, { method: 'POST', body: {} });

export const recibirPedido = (id: string, recibidas?: Array<{ id: string; cantidad: number }>): Promise<Pedido> =>
  apiFetch<Pedido>(`${BASE}/pedidos/${id}/recibir`, { method: 'POST', body: recibidas ? { recibidas } : {} });
